import { useMemo, useState } from 'react'
import { useFullAPContent } from '@/hooks/useFullAPContent'
import { isTruncatedBridgedPost, stripQuoteReplyMarkerFromHtml } from '@/lib/bridgedContent'

interface FullAPContentExpanderProps {
    /** The original AP URL (post or profile) */
    bridgyOriginalUrl: string | undefined
    /** The AT Protocol text — used to detect truncation and compare length */
    text: string
    /**
     * True when the view renders a quote card alongside this text. The full AP
     * content then still carries the fediverse client's "RE: <url>" marker
     * pointing at that same post, which the card already conveys — so it's
     * stripped from the expanded content as boilerplate. Left false when
     * nothing else conveys the reference (then the marker is the only pointer
     * to it), and for profile bios, which have no quote reference at all.
     */
    hasQuoteReference?: boolean
    /** Normal text render — shown when not truncated or collapsed */
    children: React.ReactNode
}

export function FullAPContentExpander({
    bridgyOriginalUrl,
    text,
    hasQuoteReference = false,
    children,
}: FullAPContentExpanderProps) {
    const [enabled, setEnabled] = useState(false)
    const [expanded, setExpanded] = useState(false)

    const isTruncated = isTruncatedBridgedPost(text)
    const originalApUrl = isTruncated ? bridgyOriginalUrl : undefined

    const { fullContentHtml, isLoading, isError, hasMore, canFetch } = useFullAPContent(
        originalApUrl,
        text,
        enabled
    )

    const displayHtml = useMemo(
        () =>
            fullContentHtml && hasQuoteReference
                ? stripQuoteReplyMarkerFromHtml(fullContentHtml)
                : fullContentHtml,
        [fullContentHtml, hasQuoteReference]
    )

    // Not truncated — render children as-is
    if (!isTruncated) {
        return <>{children}</>
    }

    // Fetched and confirmed no extra content — just show children
    if (enabled && !isLoading && !hasMore && !isError) {
        return <>{children}</>
    }

    return (
        <>
            {expanded && displayHtml ? (
                <div
                    className="text-sm text-text-primary [&_a]:text-primary [&_a]:hover:underline [&_p]:mb-2 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: displayHtml }}
                />
            ) : (
                children
            )}

            {canFetch && (
                <button
                    type="button"
                    onClick={e => {
                        e.stopPropagation()
                        if (!enabled) {
                            setEnabled(true)
                            setExpanded(true)
                        } else {
                            setExpanded(prev => !prev)
                        }
                    }}
                    className="mt-1 text-xs text-primary hover:underline focus:outline-none"
                >
                    {expanded ? 'Show less' : 'Show more'}
                </button>
            )}

            {isError && (
                <span className="mt-1 text-xs text-text-secondary">
                    Could not load full content.
                </span>
            )}
        </>
    )
}
